import Quill from 'quill';
import {
    Component,
    forwardRef,
    input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
} from '@angular/forms';
import { StyleAttributor } from 'parchment';
import { NgClass } from '@angular/common';

const AlignStyle = new StyleAttributor('align', 'text-align', {
    scope: Quill.import('parchment').Scope.BLOCK,
    whitelist: ['right', 'center', 'justify', 'left'],
});

Quill.register(AlignStyle, true);

/**
 * Editor de texto rico (HTML) baseado no Quill, integrável a formulários
 * (`ControlValueAccessor`).
 *
 * Opcionalmente limita o conteúdo a {@link maxLength} caracteres e normaliza os
 * espaços não separáveis (`&nbsp;`) ao gerar o HTML semântico.
 */
@Component({
    selector: 'ai-text-editor',
    standalone: true,
    imports: [QuillModule, FormsModule, ReactiveFormsModule, NgClass],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextEditor),
            multi: true,
        },
    ],
    templateUrl: './text-editor.component.html',
    styleUrls: ['./text-editor.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
})
export class TextEditor implements OnInit, ControlValueAccessor {
    /** Limite máximo de caracteres (null = sem limite). */
    public maxLength = input<number | null>(null);
    @ViewChild('editor', { static: true })
    protected editorComponent!: QuillEditorComponent;
    protected editorControl = new FormControl<string>('');
    private onChange: (value: string) => void = () => {};
    private onTouched: () => void = () => {};

    ngOnInit(): void {
        this.editorComponent.onContentChanged.subscribe((event) => {
            // Só propaga mudanças originadas do USUÁRIO. O Quill dispara
            // `onContentChanged` também quando o conteúdo é definido
            // programaticamente (`writeValue`, com `source === 'api'`); propagar
            // isso chamaria `onChange` e marcaria o formulário como alterado
            // (dirty) na carga, sem o usuário ter mexido.
            if (event.source === 'user') {
                this.onChange(event.html ?? '');
            }
        });

        this.editorComponent.onEditorCreated.subscribe({
            next: (quill: any) => {
                quill.legacyGetSemanticHTML = quill.getSemanticHTML;
                quill.getSemanticHTML = (a: number, b: number) =>
                    quill
                        .legacyGetSemanticHTML(a, b)
                        .replaceAll(/((?:&nbsp;)*)&nbsp;/g, '$1 ');

                quill.on('text-change', () => {
                    const maxLength = this.maxLength();

                    if (maxLength && maxLength! > 0) {
                        if (quill.getLength() > maxLength!) {
                            quill.deleteText(maxLength - 1, quill.getLength());
                        }
                    }
                });
            },
        });
    }

    /**
     * Implementação do writeValue do ControlValueAcessor.
     * Define o valor do editor de texto com o conteúdo fornecido.
     *
     * @param value O valor a ser definido no editor de texto.
     */
    writeValue(value: string): void {
        this.editorControl.setValue(value, { emitEvent: false });
    }

    /**
     * Implementação do registerOnChange do ControlValueAcessor.
     * Registra uma função de callback que será chamada quando o valor do editor de texto mudar.
     *
     * @param fn A função de callback a ser registrada.
     */
    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    /**
     * Implementação do registerOnTouched do ControlValueAcessor.
     * Registra uma função de callback que será chamada quando o editor de texto for tocado.
     *
     * @param fn A função de callback a ser registrada.
     */
    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    /**
     * Implementação do setDisabledState do ControlValueAcessor.
     * Define o estado do editor de texto como desabilitado ou habilitado.
     *
     * @param isDisabled Indica se o editor de texto deve ser desabilitado.
     */
    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.editorControl.disable() : this.editorControl.enable();
    }

    /**
     * Trata o clique nos botões de formatação de texto.
     *
     * @param command O comando de formatação a ser executado.
     * @param value O valor a ser aplicado ao comando, se necessário.
     */
    execCommand(command: string, value: any = null): void {
        const editor = this.editorComponent.quillEditor;
        editor.format(command, value ?? !editor.getFormat()[command]);
    }

    /**
     * Executa o comando de alinhamento no editor de texto.
     *
     * @param value O valor de alinhamento a ser aplicado ao editor de texto.
     */
    execAlign(value: string): void {
        const editor = this.editorComponent.quillEditor;
        editor.format('align', value);
    }

    /**
     * Alterna entre listas ordenadas e não ordenadas no editor de texto.
     *
     * @param type O tipo de lista a ser aplicada ('ordered' ou 'bullet').
     */
    toggleList(type: 'ordered' | 'bullet'): void {
        const editor = this.editorComponent.quillEditor;
        const selection = editor.getSelection();

        if (!selection) return;

        const formats = editor.getFormat(selection);
        const isSameType = formats['list'] === type;

        editor.format('list', isSameType ? false : type);

        if (type === 'bullet') {
            this.correctLists();
        }
    }

    /**
     * Corrige a exibição das listas ordenadas/bullets.
     */
    private correctLists(): void {
        const editor = this.editorComponent.quillEditor;
        const html = editor.root.innerHTML;
        const htmlCorrigido = html
            .replace('<ol>', '<ul>')
            .replace('<\/ol>', '</ul>')
            .replace('<li data-list="bullet">', '<li>');

        editor.setContents([]);
        editor.clipboard.dangerouslyPasteHTML(0, htmlCorrigido, 'user');
    }
}
